import { useEffect } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { CheckCircle, ChevronRight, MapPin } from 'lucide-react'
import { getCityBySlug, getNearbyCities } from '@/data/ukCities'
import { GEO_CONFIGS } from '@/data/geoConfig'
import SiluxPromise from '@/components/SiluxPromise'
import { setMetaTags, injectSchema } from '@/utils/seo'

/**
 * Generic GEO city landing page. Renders the same structure as the bespoke
 * GeoSimCards/GeoBroadband pages, driven entirely by a GEO_CONFIGS entry. One route
 * per config (App.tsx) passes its key here.
 */
export default function GeoLanding({ configKey }: { configKey: string }) {
  const cfg = GEO_CONFIGS[configKey]
  const { city: citySlug } = useParams<{ city: string }>()
  const city = getCityBySlug(citySlug ?? '')

  // Unknown config or unknown city → send back to the parent landing page.
  if (!cfg) return <Navigate to="/" replace />
  if (!city) return <Navigate to={`/${cfg.slug}`} replace />

  const t = cfg.theme
  const nearby = getNearbyCities(city)
  const regionLabel =
    city.region === 'Scotland' ? 'Scotland'
      : city.region === 'Wales' ? 'Wales'
        : city.region === 'Northern Ireland' ? 'Northern Ireland'
          : 'England'

  const base = 'https://www.siluxmobile.co.uk'
  const canonical = `${base}/${cfg.slug}/${city.slug}`
  const faqs = cfg.faqs(city, regionLabel)
  const schemaId = `geo-${cfg.slug}-schema`

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: `${cfg.label} in ${city.name}`,
        provider: { '@type': 'Organization', name: 'Silux Mobile', url: base },
        serviceType: cfg.serviceType,
        areaServed: { '@type': 'City', name: city.name, containedInPlace: { '@type': 'Country', name: 'United Kingdom' } },
        description: cfg.description(city),
        url: canonical,
        offers: { '@type': 'AggregateOffer', lowPrice: cfg.priceLow, highPrice: cfg.priceHigh, priceCurrency: 'GBP' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: base },
          { '@type': 'ListItem', position: 2, name: cfg.label, item: `${base}/${cfg.slug}` },
          { '@type': 'ListItem', position: 3, name: city.name, item: canonical },
        ],
      },
    ],
  }

  useEffect(() => {
    setMetaTags({
      title: cfg.title(city),
      description: cfg.description(city),
      keywords: cfg.keywords(city),
      canonical,
      ogUrl: canonical,
    })
    injectSchema(JSON.stringify(schema), schemaId)
    return () => { const el = document.getElementById(schemaId); if (el) el.remove() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.slug, city.slug])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className={`bg-gradient-to-br ${t.heroGradient} text-white py-16 px-4`}>
        <div className="max-w-5xl mx-auto">
          <nav className={`${t.crumbText} text-sm mb-6 flex items-center gap-2 flex-wrap`}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/${cfg.slug}`} className="hover:text-white">{cfg.label}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{city.name}</span>
          </nav>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className={`w-5 h-5 ${t.crumbText}`} />
            <span className={`${t.crumbText} text-sm`}>{city.county}, {regionLabel}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{cfg.h1(city)}</h1>
          <p className={`text-xl ${t.heroLeadText} mb-3 max-w-2xl`}>{cfg.heroLead(city)}</p>
          <p className={`${t.heroSubText} text-sm mb-8 max-w-2xl`}>{cfg.heroSub(city)}</p>
          <div className="flex flex-wrap gap-3">
            <Link to={cfg.plansCtaTo} className={`${t.ctaPrimary} font-semibold px-6 py-3 rounded-lg transition`}>
              See All Plans
            </Link>
            <Link to="/network-coverage" className="border border-white/50 text-white px-6 py-3 rounded-lg hover:bg-white/10 transition text-sm">
              Check {city.name} Coverage
            </Link>
          </div>
        </div>
      </section>

      {/* Why Silux in this city */}
      <section className={`py-14 px-4 ${t.whyBg}`}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{cfg.whyTitle(city)}</h2>
          <div className="grid md:grid-cols-4 gap-5">
            {cfg.whyCards(city).map(c => (
              <div key={c.title} className="bg-white rounded-xl p-5 shadow-sm text-center">
                <div className="text-3xl mb-3">{c.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{c.title}</h3>
                <p className="text-gray-500 text-xs">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiluxPromise variant={cfg.kind} />

      {/* Plans */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">{cfg.plansHeading(city)}</h2>
          <p className="text-gray-500 text-center mb-10">{cfg.plansSub}</p>
          <div className="grid md:grid-cols-3 gap-6">
            {cfg.plans.map(plan => (
              <div key={plan.name} className={`rounded-2xl p-6 border-2 ${plan.popular ? `${t.popularBorder} shadow-lg` : 'border-gray-200'} relative`}>
                {plan.popular && <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${t.popularBadge} text-white text-xs font-bold px-3 py-1 rounded-full`}>MOST POPULAR</div>}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-2">{plan.data}</p>
                <p className={`text-3xl font-bold ${t.priceText} mb-4`}>{plan.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className={`w-4 h-4 ${t.checkText} flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={cfg.plansCtaTo} className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition ${plan.popular ? t.planBtnPopular : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Order to {city.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-14 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{cfg.coverageTitle(city)}</h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">{cfg.coverageBody(city)}</p>
          <Link to="/network-coverage" className={`inline-block ${t.coverageBtn} text-white font-semibold px-6 py-3 rounded-lg transition`}>
            Check Your {city.name} Postcode
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{cfg.label} in {city.name} — FAQs</h2>
          <div className="space-y-3">
            {faqs.map(f => (
              <details key={f.q} className="bg-gray-50 rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center text-sm">
                  {f.q}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-2" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      {nearby.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">{cfg.label} Near {city.name}</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {nearby.map(c => (
                <Link key={c.slug} to={`/${cfg.slug}/${c.slug}`} className={`bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-700 ${t.nearbyHover} transition`}>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cross-links */}
      <section className={`py-12 px-4 ${t.crossBg} text-white`}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold mb-4 text-center">More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cfg.crossLinks(city).map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
